import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { UpgradeModal } from "@/components/ui/UpgradeModal";
import type { SectionType } from "@profolio/validators";
import { mapSubscriptionToProductTier } from "@profolio/validators/tiers";
import type { ProfileData } from "@profolio/templates";
import type { EditorDraft } from "@/lib/editor/editor-types";
import {
  ProfileDraftProvider,
  type WorkspaceTab,
  type PublishStatus,
  type ProfileDraftContextValue,
  type PendingNavigation,
 } from "@/context/ProfileDraftContext";
import type { SelectedNode, InspectorTab } from "@/lib/editor/types";
import { EditorShell } from "@/components/layout/EditorShell";
import { DesignNavigationGuardDialog } from "@/components/design/DesignNavigationGuardDialog";
import { ConflictResolutionModal } from "@/components/dialogs/ConflictResolutionModal";
import { normalizeServerToDraft, composeEffectiveDraft } from "@/lib/editor/draft-normalizer";
import { useEditorDraft } from "@/lib/editor/use-editor-draft";
import { useAutosave } from "@/lib/editor/use-autosave";
import { getPublicSiteUrl, getDisplayDomain } from "@/lib/utils/url";
import { Loader2 } from "lucide-react";

export function EditProfilePage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  // Queries
  const { data: profile, isLoading: isProfileLoading, isError: isProfileError, error: profileError, refetch: refetchProfile } = trpc.profile.getMyProfile.useQuery(undefined, {
    refetchInterval: 3000,
  });
  const { data: dbSections = [], isLoading: isSectionsLoading, isError: isSectionsError, error: sectionsError, refetch: refetchSections } = trpc.profile.getSections.useQuery();
  const isLoading = isProfileLoading || isSectionsLoading;
  const isError = isProfileError || isSectionsError;
  const errorMsg = profileError?.message || sectionsError?.message;

  // Single-source snapshot
  const serverSnapshot = useMemo(() => {
    if (!profile || isSectionsLoading) return null;
    return normalizeServerToDraft(profile, dbSections);
  }, [profile, dbSections, isSectionsLoading]);

  // Unified Draft Reducer State
  const { state: editorState, dispatch } = useEditorDraft(serverSnapshot);
  const draft = editorState.draft;

  // New Shell State
  const [selectedNode, setSelectedNodeState] = useState<SelectedNode | null>({ kind: "overview" });
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>("content");

  // ── Profile-Scoped Section Persistence & Unified State ──
  const profileStorageKey = useMemo(() => {
    return profile?.id ? `profolio_active_section_id:${profile.id}:content` : null;
  }, [profile?.id]);

  // Derive legacy states
  const activeTab = useMemo<WorkspaceTab>(() => {
    if (
      selectedNode?.kind === "siteStyle" ||
      selectedNode?.kind === "header" ||
      selectedNode?.kind === "effects" ||
      selectedNode?.kind === "footer" ||
      selectedNode?.kind === "advanced"
    ) return "design";
    if (selectedNode?.kind === "section") return "content";
    if (selectedNode?.kind === "pages") return "pages";
    if (selectedNode?.kind === "publish") return "publish";
    return "home";
  }, [selectedNode]);

  const selectedSectionId = selectedNode?.kind === "section" ? selectedNode.sectionId : null;

  const setSelectedNode = useCallback((node: SelectedNode | null) => {
    setSelectedNodeState(node);
    if (node?.kind === "section" && profileStorageKey) {
      try {
        localStorage.setItem(profileStorageKey, node.sectionId);
      } catch {}
    }
  }, [profileStorageKey]);

  // Restore persisted section or default to first section when profile & draft load
  const hasRestoredSectionRef = useRef(false);
  useEffect(() => {
    if (!profileStorageKey || !draft?.sections?.length || hasRestoredSectionRef.current) return;
    try {
      const savedSectionId = localStorage.getItem(profileStorageKey);
      if (savedSectionId) {
        const match = draft.sections.find((s) => s.id === savedSectionId);
        if (match) {
          hasRestoredSectionRef.current = true;
          setSelectedNodeState({ kind: "section", sectionId: match.id, clientKey: match.clientKey });
          return;
        }
      }
      if (draft.sections[0]) {
        hasRestoredSectionRef.current = true;
        setSelectedNodeState({ kind: "section", sectionId: draft.sections[0].id, clientKey: draft.sections[0].clientKey });
      }
    } catch {}
  }, [profileStorageKey, draft?.sections]);

  const setActiveTab = useCallback((tab: WorkspaceTab) => {
    if (tab === "home") setSelectedNode({ kind: "overview" });
    if (tab === "design") setSelectedNode({ kind: "siteStyle" });
    if (tab === "pages") setSelectedNode({ kind: "pages" });
    if (tab === "publish") setSelectedNode({ kind: "publish" });
    if (tab === "content") {
      if (draft?.sections?.[0]) {
        setSelectedNode({ kind: "section", sectionId: draft.sections[0].id, clientKey: draft.sections[0].clientKey });
      } else {
        setSelectedNode({ kind: "overview" });
      }
    }
  }, [setSelectedNode, draft?.sections]);

  const setSelectedSectionId = useCallback((id: string | null) => {
    if (id) {
      const match = draft?.sections.find(s => s.id === id);
      setSelectedNode({ kind: "section", sectionId: id, clientKey: match?.clientKey });
    } else {
      setSelectedNode({ kind: "overview" });
    }
  }, [setSelectedNode, draft?.sections]);

  // Synchronize remapped section IDs
  const handleIdMappings = useCallback((idMappings: Record<string, string>) => {
    setSelectedNodeState((currentNode) => {
      if (currentNode?.kind === "section" && idMappings[currentNode.sectionId]) {
        const newId = idMappings[currentNode.sectionId];
        if (profileStorageKey) {
          try {
            localStorage.setItem(profileStorageKey, newId);
          } catch {}
        }
        return { kind: "section", sectionId: newId, clientKey: currentNode.clientKey };
      }
      return currentNode;
    });

    setDesignPreviewOverride((currentOverride) => {
      if (!currentOverride?.layoutOverrides?.sectionOverridesById) return currentOverride;
      const currentOverridesById = { ...currentOverride.layoutOverrides.sectionOverridesById };
      let hasChanges = false;
      for (const [tempId, realId] of Object.entries(idMappings)) {
        if (currentOverridesById[tempId]) {
          currentOverridesById[realId] = currentOverridesById[tempId];
          delete currentOverridesById[tempId];
          hasChanges = true;
        }
      }
      if (!hasChanges) return currentOverride;
      return {
        ...currentOverride,
        layoutOverrides: {
          ...currentOverride.layoutOverrides,
          sectionOverridesById: currentOverridesById,
        },
      };
    });

    setTransientSectionOverrides((currentOverrides) => {
      let hasChanges = false;
      const nextOverrides = { ...currentOverrides };
      for (const [tempId, realId] of Object.entries(idMappings)) {
        if (nextOverrides[tempId]) {
          nextOverrides[realId] = nextOverrides[tempId];
          delete nextOverrides[tempId];
          hasChanges = true;
        }
      }
      return hasChanges ? nextOverrides : currentOverrides;
    });
  }, [profileStorageKey]);

  const [previewOpen, setPreviewOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 1280 : false
  );
  const [previewViewport, setPreviewViewport] = useState<"desktop" | "tablet" | "mobile">("desktop");
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [proViolations, setProViolations] = useState<string[]>([]);

  // ── Transient Design State (Quality Bar Pillar 5) ──
  const [designPreviewOverride, setDesignPreviewOverride] = useState<Partial<ProfileData> | null>(null);
  const [transientSectionOverrides, setTransientSectionOverrides] = useState<Record<string, any>>({});
  const [activeDesignSectionType, setActiveDesignSectionType] = useState<string | null>(null);

  // ── Centralized Dual-Rail & Section Navigation State ──
  const [isPrimaryRailExpanded, setIsPrimaryRailExpanded] = useState(false);
  const [isContentNavCollapsed, setIsContentNavCollapsed] = useState(false);
  const [isAppearanceOverlayOpen, setIsAppearanceOverlayOpen] = useState(false);

  const navigateToSectionDesign = useCallback((sectionType: string) => {
    setActiveDesignSectionType(sectionType);
    setActiveTab("design");
  }, []);

  // ── Universal Navigation Guard State (Phase A) ──
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
  const [isNavigationGuardOpen, setIsNavigationGuardOpen] = useState(false);
  const [isApplyingAndNavigating, setIsApplyingAndNavigating] = useState(false);

  // ── Conflict Resolution Modal State (Phase B) ──
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);


  // Derive effective preview draft (committed draft + transient design overrides)
  const effectivePreviewDraft = useMemo<EditorDraft | null>(() => {
    if (!draft) return null;
    return composeEffectiveDraft(draft, designPreviewOverride, transientSectionOverrides);
  }, [draft, designPreviewOverride, transientSectionOverrides]);

  const hasTransientDesignChanges = Boolean(
    (designPreviewOverride && Object.keys(designPreviewOverride).length > 0) ||
    Object.keys(transientSectionOverrides).length > 0
  );

  const applyDesignChanges = useCallback(() => {
    if (designPreviewOverride && Object.keys(designPreviewOverride).length > 0) {
      dispatch({
        type: "UPDATE_PROFILE",
        payload: designPreviewOverride,
      });
    }

    if (Object.keys(transientSectionOverrides).length > 0) {
      for (const [sectionId, override] of Object.entries(transientSectionOverrides)) {
        const section = draft?.sections.find(s => s.id === sectionId);
        if (section) {
          dispatch({
            type: "UPDATE_SECTION_CONTENT",
            payload: {
              sectionId,
              content: { ...(section.content as object), ...override },
            },
          });
        }
      }
    }

    setDesignPreviewOverride(null);
    setTransientSectionOverrides({});
    toast.success("Design settings applied to draft!");
  }, [designPreviewOverride, transientSectionOverrides, dispatch, draft]);

  const discardDesignChanges = useCallback(() => {
    setDesignPreviewOverride(null);
    setTransientSectionOverrides({});
    toast.info("Transient design changes discarded.");
  }, []);

  const handleUpgradeClick = useCallback((featureKey?: string) => {
    setProViolations(featureKey ? [featureKey] : []);
    setIsUpgradeModalOpen(true);
  }, []);

  const handleProFeatureViolation = useCallback((violationsOrError: string[] | unknown) => {
    let violations: string[] = [];
    if (Array.isArray(violationsOrError)) {
      violations = violationsOrError;
    } else if (violationsOrError && typeof violationsOrError === "object") {
      violations =
        (violationsOrError as any)?.cause?.violations ||
        (violationsOrError as any)?.data?.cause?.violations ||
        (violationsOrError as any)?.shape?.data?.cause?.violations ||
        [];
    }
    setProViolations(violations);
    setIsUpgradeModalOpen(true);
  }, []);

  // Mount autosave strictly once at the parent level (persists committed draft only)
  const {
    status: autosaveStatus,
    flush,
    saveSnapshot,
    conflictResult,
    resolveConflictKeepLocal,
    resolveConflictUseServer,
  } = useAutosave(editorState, dispatch, {
    enabled: true,
    onIdMappings: handleIdMappings,
    onProFeatureRequired: handleProFeatureViolation,
  });

  // Flush pending changes when the user switches tabs or minimizes the window
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flush().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [flush]);

  // Automatically open conflict modal if collisions occur
  useEffect(() => {
    if (conflictResult?.hasCollisions) {
      setIsConflictModalOpen(true);
    }
  }, [conflictResult]);

  // Publish Mutation
  const publishMutation = trpc.publish.publish.useMutation({
    onSuccess: () => {
      toast.success("Academic site published live successfully!");
      utils.profile.getMyProfile.invalidate();
      utils.profile.getSections.invalidate();
    },
    onError: (error) => {
      if (error.message === "PRO_FEATURE_REQUIRED") {
        handleProFeatureViolation(error);
        return;
      }
      toast.error(error.message || "Failed to publish site.");
    },
  });

  const [isPublishingFlow, setIsPublishingFlow] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  // Dedicated Save Draft action: flushes autosave, saves snapshot with design preview, clears override
  const handleSaveDraft = useCallback(async (): Promise<void> => {
    if (autosaveStatus === "conflict") {
      setIsConflictModalOpen(true);
      toast.error("Please resolve version conflicts before saving.");
      return;
    }

    if (!draft) return;

    setIsSavingDraft(true);
    try {
      const snapshot = composeEffectiveDraft(draft, designPreviewOverride, transientSectionOverrides);
      const confirmedVersion = await saveSnapshot(snapshot);
      setDesignPreviewOverride(null);
      setTransientSectionOverrides({});
      dispatch({
        type: "DRAFT_SAVE_CONFIRMED",
        payload: {
          snapshot,
          confirmedVersion,
        },
      });
      utils.profile.getMyProfile.invalidate();
      utils.profile.getSections.invalidate();
      toast.success("Draft saved successfully!");
    } catch (err: unknown) {
      if ((err as Error)?.message === "PRO_FEATURE_REQUIRED") {
        handleProFeatureViolation(err);
        return;
      }
      const msg = err instanceof Error ? err.message : "Failed to save draft.";
      toast.error(msg);
    } finally {
      setIsSavingDraft(false);
    }
  }, [
    autosaveStatus,
    draft,
    designPreviewOverride,
    flush,
    saveSnapshot,
    dispatch,
    utils,
    transientSectionOverrides,
    handleProFeatureViolation,
  ]);

  const handlePublish = useCallback(async (): Promise<void> => {
    if (autosaveStatus === "conflict") {
      setIsConflictModalOpen(true);
      toast.error("Please resolve version conflicts before publishing.");
      return;
    }
    if (autosaveStatus === "error") {
      toast.error("Cannot publish while draft has save errors. Please resolve errors first.");
      return;
    }

    if (!draft) return;

    setIsPublishingFlow(true);
    try {
      // 1. Flush any pending autosaves
      await flush();

      // 2. Compose exact snapshot including any transient design changes
      const snapshot = composeEffectiveDraft(draft, designPreviewOverride, transientSectionOverrides);

      // 3. Persist snapshot synchronously through the single-flight autosave mutex loop
      const confirmedVersion = await saveSnapshot(snapshot);

      // 4. Clear transient design state & synchronize local reducer state with confirmed revision
      setDesignPreviewOverride(null);
      setTransientSectionOverrides({});
      dispatch({
        type: "DRAFT_SAVE_CONFIRMED",
        payload: {
          snapshot,
          confirmedVersion,
        },
      });

      // 5. Trigger live publish mutation passing the exact confirmed revision
      await publishMutation.mutateAsync({ expectedDraftVersion: confirmedVersion });
      utils.profile.getMyProfile.invalidate();
      utils.profile.getSections.invalidate();
      utils.pages.listPages.invalidate();
    } catch (err: unknown) {
      if ((err as Error)?.message === "PRO_FEATURE_REQUIRED") {
        handleProFeatureViolation(err);
        return;
      }
      const msg = err instanceof Error ? err.message : "Failed to save or publish changes.";
      toast.error(msg);
    } finally {
      setIsPublishingFlow(false);
    }
  }, [
    autosaveStatus,
    draft,
    designPreviewOverride,
    flush,
    saveSnapshot,
    dispatch,
    publishMutation,
    utils,
    transientSectionOverrides,
    handleProFeatureViolation,
  ]);

  // Navigation execution helper - FIX: nav.destination === "publish" must NOT publish automatically
  const executeNavigation = useCallback(
    (nav: PendingNavigation) => {
      if (nav.source === "publish_action") {
        handlePublish();
        return;
      }
      if (nav.destination === "publish") {
        // Publish is now handled as an action in the toolbar
        return;
      }
      if (nav.destinationType === "workspace") {
        setActiveTab(nav.destination as WorkspaceTab);
      } else if (nav.destinationType === "route") {
        if (nav.destination === "back") {
          window.history.back();
        } else {
          navigate(String(nav.destination));
        }
      }
    },
    [navigate, handlePublish]
  );

  // Guarded request navigation
  const requestNavigation = useCallback(
    (nav: PendingNavigation) => {
      if (hasTransientDesignChanges) {
        setPendingNavigation(nav);
        setIsNavigationGuardOpen(true);
      } else {
        executeNavigation(nav);
      }
    },
    [hasTransientDesignChanges, executeNavigation]
  );

  // Confirm Apply & Navigate (Asynchronous contract)
  const confirmApplyAndNavigate = useCallback(async () => {
    if (!pendingNavigation) return;
    if (!designPreviewOverride && Object.keys(transientSectionOverrides).length === 0) return;
    setIsApplyingAndNavigating(true);
    try {
      if (designPreviewOverride && Object.keys(designPreviewOverride).length > 0) {
        dispatch({
          type: "UPDATE_PROFILE",
          payload: designPreviewOverride,
        });
      }
      if (Object.keys(transientSectionOverrides).length > 0) {
        for (const [sectionId, override] of Object.entries(transientSectionOverrides)) {
          const section = draft?.sections.find(s => s.id === sectionId);
          if (section) {
            dispatch({
              type: "UPDATE_SECTION_CONTENT",
              payload: {
                sectionId,
                content: { ...(section.content as object), ...override },
              },
            });
          }
        }
      }

      setDesignPreviewOverride(null);
      setTransientSectionOverrides({});
      await flush();
      const target = pendingNavigation;
      setPendingNavigation(null);
      setIsNavigationGuardOpen(false);
      setIsApplyingAndNavigating(false);
      executeNavigation(target);
      toast.success("Design customizations applied and saved!");
    } catch (err: unknown) {
      setIsApplyingAndNavigating(false);
      const msg = err instanceof Error ? err.message : "Failed to save design changes. Please review and try again.";
      toast.error(msg);
    }
  }, [designPreviewOverride, pendingNavigation, dispatch, flush, executeNavigation]);

  // Confirm Discard & Navigate
  const confirmDiscardAndNavigate = useCallback(() => {
    const target = pendingNavigation;
    setDesignPreviewOverride(null);
    setTransientSectionOverrides({});
    setPendingNavigation(null);
    setIsNavigationGuardOpen(false);
    if (target) {
      executeNavigation(target);
    }
    toast.info("Transient design changes discarded.");
  }, [pendingNavigation, executeNavigation]);

  // Cancel Navigation ("Stay Editing" / Escape / Backdrop click)
  const cancelNavigation = useCallback(() => {
    setPendingNavigation(null);
    setIsNavigationGuardOpen(false);
    setIsApplyingAndNavigating(false);
  }, []);

  // Browser beforeunload listener when transient changes exist
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasTransientDesignChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasTransientDesignChanges]);

  // Browser popstate (Back/Forward) guard
  useEffect(() => {
    if (!hasTransientDesignChanges) return;

    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      requestNavigation({ destination: "back", destinationType: "route", source: "browser_history" });
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [hasTransientDesignChanges, requestNavigation]);

  // Hydrate selectedSectionId from profile-scoped storage once draft is available
  const isHydratedSectionRef = useRef(false);
  useEffect(() => {
    if (!draft || draft.sections.length === 0 || isHydratedSectionRef.current) return;

    if (profileStorageKey) {
      try {
        const saved = localStorage.getItem(profileStorageKey);
        const match = draft.sections.find(
          (s) => s.id === saved || (s.clientKey && s.clientKey === saved)
        );
        if (match) {
          setSelectedSectionId(match.id);
          isHydratedSectionRef.current = true;
          return;
        }
      } catch {}
    }

    if (!selectedSectionId) {
      setSelectedSectionId(draft.sections[0].id);
      isHydratedSectionRef.current = true;
    }
  }, [draft, profileStorageKey, selectedSectionId]);

  const rawTier = (profile?.userTier as string) || "starter";
  const userTier = mapSubscriptionToProductTier(rawTier);
  const publishedUrl = getPublicSiteUrl(profile?.subdomain);
  const displayDomain = getDisplayDomain(profile?.subdomain);

  // Change detection
  const isDirty = editorState.editSequence !== editorState.savedSequence;

  const isDesignDirty = useMemo(() => {
    if (!draft?.profile || !profile) return false;
    const d = draft.profile;
    const p = profile;
    return (
      d.themeKey !== p.themeKey ||
      d.colorPalette !== p.colorPalette ||
      d.fontPairing !== p.fontPairing ||
      d.backgroundStyle !== p.backgroundStyle ||
      d.layoutDensity !== p.layoutDensity ||
      d.cornerRadius !== p.cornerRadius ||
      d.sectionArrangement !== p.sectionArrangement ||
      d.animationLevel !== p.animationLevel ||
      JSON.stringify(d.layoutOverrides || {}) !== JSON.stringify(p.layoutOverrides || {}) ||
      JSON.stringify(d.customColors || null) !== JSON.stringify(p.customColors || null) ||
      JSON.stringify(d.seoOverrides || {}) !== JSON.stringify(p.seoOverrides || {})
    );
  }, [draft?.profile, profile]);

  const isContentDirty = isDirty;

  const handleDiscardDraft = useCallback(async (): Promise<void> => {
    try {
      setDesignPreviewOverride(null);
      const [freshProfile, freshSections] = await Promise.all([
        utils.profile.getMyProfile.fetch(),
        utils.profile.getSections.fetch(),
      ]);
      if (freshProfile && freshSections) {
        const freshDraft = normalizeServerToDraft(freshProfile, freshSections);
        dispatch({ type: "SET_INITIAL_DRAFT", payload: freshDraft });
        toast.info("Draft discarded and restored from server.");
      }
    } catch {
      if (profile && dbSections) {
        dispatch({
          type: "SET_INITIAL_DRAFT",
          payload: normalizeServerToDraft(profile, dbSections),
        });
        toast.info("Draft restored.");
      }
    }
  }, [utils, profile, dbSections, dispatch]);

  const hasUnsavedChanges = isDirty || Boolean(hasTransientDesignChanges);
  const currentDraftVersion = Math.max(
    editorState.acknowledgedRevision ?? editorState.draft?.draftVersion ?? 1,
    profile?.draftVersion ?? 1
  );
  const hasUnpublishedChanges =
    hasUnsavedChanges ||
    currentDraftVersion > (profile?.publishedDraftVersion ?? 0);
  const isPublished = Boolean(profile?.published);

  const publishStatus: PublishStatus = isPublishingFlow || publishMutation.isPending
    ? "publishing"
    : publishMutation.isError
    ? "error"
    : hasUnpublishedChanges
    ? "publish_pending"
    : "published";

  // Navigation jump helper for Home checklist
  const handleNavigateToSection = useCallback(
    (type: SectionType) => {
      const match = draft?.sections.find((s) => s.type === type);
      if (match) {
        setSelectedSectionId(match.id);
      }
      requestNavigation({ destination: "content", destinationType: "workspace", source: "home_checklist" });
    },
    [draft?.sections, setSelectedSectionId, requestNavigation]
  );

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

      // Cmd/Ctrl + S -> Save Draft
      if (cmdOrCtrl && e.key === "s") {
        e.preventDefault();
        handleSaveDraft().catch(() => {});
        return;
      }

      // Cmd/Ctrl + Shift + P -> Publish
      if (cmdOrCtrl && e.shiftKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        if (!publishMutation.isPending && !isPublishingFlow && autosaveStatus !== "saving") {
          handlePublish();
        }
        return;
      }

      // Cmd/Ctrl + ` -> Toggle preview
      if (cmdOrCtrl && e.key === "`") {
        e.preventDefault();
        setPreviewOpen((prev) => !prev);
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleSaveDraft, handlePublish, publishMutation.isPending, isPublishingFlow, autosaveStatus]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
          <p className="text-xs font-medium text-muted-foreground">Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-center max-w-sm px-4">
          <div className="rounded-full bg-destructive/10 p-3 text-destructive">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <p className="text-sm font-medium text-foreground">Failed to load workspace</p>
          <p className="text-xs text-muted-foreground">{errorMsg || "Network connection failed"}</p>
          <button
            onClick={() => { refetchProfile(); refetchSections(); }}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground text-xs font-medium rounded-md hover:bg-primary/90 transition-colors"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  if (!profile || !draft) {
    return (
      <div className="flex h-screen items-center justify-center text-sm font-medium">
        No profile found. Please create a profile first.
      </div>
    );
  }

  const contextValue: ProfileDraftContextValue = {
    serverSnapshot,
    lastSavedDraft: editorState.lastSavedDraft ?? serverSnapshot,
    draft,
    editorState,
    dispatch,
    autosaveStatus,
    publishStatus,
    activeTab,
    setActiveTab,
    activeSectionId: selectedSectionId,
    setActiveSectionId: setSelectedSectionId,
    previewOpen,
    setPreviewOpen,
    previewViewport,
    setPreviewViewport,
    userTier,
    flushAutosave: flush,
    handleSaveDraft,
    isSavingDraft,
    handlePublish,
    isPublishing: publishMutation.isPending || isPublishingFlow,
    publishedUrl,
    displayDomain,
    isDirty,
    isDesignDirty,
    isContentDirty,
    hasUnsavedChanges,
    hasUnpublishedChanges,
    isPublished,
    onNavigateToSection: handleNavigateToSection,
    navigateToSectionDesign,
    activeDesignSectionType,
    setActiveDesignSectionType,
    onDiscardDraft: handleDiscardDraft,

    // Centralized Dual-Rail & Section Navigation State
    isPrimaryRailExpanded,
    setIsPrimaryRailExpanded,
    isContentNavCollapsed,
    setIsContentNavCollapsed,
    isAppearanceOverlayOpen,
    setIsAppearanceOverlayOpen,
    selectedSectionId,
    setSelectedSectionId,
    selectedNode,
    setSelectedNode,
    inspectorTab,
    setInspectorTab,

    // Transient Design State
    designPreviewOverride,
    setDesignPreviewOverride,
    transientSectionOverrides,
    setTransientSectionOverrides,
    effectivePreviewDraft,
    applyDesignChanges,
    discardDesignChanges,
    hasTransientDesignChanges,
    onUpgradeClick: handleUpgradeClick,

    // Universal Navigation Guard State
    pendingNavigation,
    isNavigationGuardOpen,
    isApplyingAndNavigating,
    requestNavigation,
    confirmApplyAndNavigate,
    confirmDiscardAndNavigate,
    cancelNavigation,

    // Conflict Resolution State
    conflictResult,
    isConflictModalOpen,
    setIsConflictModalOpen,
    resolveConflictKeepLocal,
    resolveConflictUseServer,
  };

  return (
    <ProfileDraftProvider value={contextValue}>
      <EditorShell />

      {/* ── Upgrade Modal ── */}
      <UpgradeModal
        open={isUpgradeModalOpen}
        onOpenChange={setIsUpgradeModalOpen}
        violations={proViolations}
      />

      {/* ── Universal Navigation Guard Dialog (Phase A) ── */}
      <DesignNavigationGuardDialog />

      {/* ── Conflict Resolution Modal (Phase B) ── */}
      <ConflictResolutionModal
        open={isConflictModalOpen}
        onOpenChange={setIsConflictModalOpen}
        conflictResult={conflictResult}
        onKeepLocal={async () => {
          await resolveConflictKeepLocal();
          setIsConflictModalOpen(false);
        }}
        onUseServer={async () => {
          await resolveConflictUseServer();
          setIsConflictModalOpen(false);
        }}
      />
    </ProfileDraftProvider>
  );
}
